// MBA Lab — Listening Session Facilitator UI. Imports only lib/core.js and
// lib/listening-model.js. Session-only persistence, full stop - see
// listening-model.js's top-of-file comment for why this tool (unlike the
// planner) doesn't offer an opt-in "remember on this device" tier: every
// field here is real, potentially sensitive draft text, and there's no
// non-sensitive structural residue left once it's stripped.
import { sessionStore, trackToolAction } from './lib/core.js';
import {
  createInitialState,
  parseSavedState,
  serializeForSave,
  setField,
  countAnswered,
  hasAnyContent,
  buildReflectionText,
  TOTAL_FIELDS,
} from './lib/listening-model.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'listening-session-facilitator';
const AUTOSAVE_DEBOUNCE_MS = 400;

function byId(id) {
  return document.getElementById(id);
}

class ListeningSessionFacilitator {
  constructor() {
    this.cacheElements();
    this.sessionStore = sessionStore(TOOL_NAME);
    this.saveTimer = null;
    this.hasEngaged = false;
    this.dock = initToolDock({
      dock: byId('listening-dock'),
      jumpTarget: byId('listening-actions'),
      autoVisibility: false,
      hideJumpUntilComplete: true,
    });

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderProgress();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('listening-form');
    this.els = {
      progressText: byId('listening-progress-text'),
      clearBtn: byId('listening-clear'),
      copyBtn: byId('listening-copy'),
      actionFeedback: byId('listening-action-feedback'),
    };
  }

  loadInitialState() {
    const saved = this.sessionStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  applyStateToForm() {
    for (const el of this.form.querySelectorAll('[data-field-key]')) {
      el.value = this.state[el.dataset.fieldKey] ?? '';
    }
  }

  wireEvents() {
    this.form.addEventListener('input', (e) => this.handleFieldInput(e));
    this.els.clearBtn.addEventListener('click', () => this.handleClear());
    this.els.copyBtn.addEventListener('click', () => this.handleCopy());
    // Flush any pending debounced autosave immediately when the tab is about
    // to go away - a plain setTimeout debounce alone has a real, if narrow,
    // window (up to AUTOSAVE_DEBOUNCE_MS) where a just-typed edit is lost if
    // the user navigates away before it fires (audit finding, confirmed live:
    // typing then reloading inside that window reverted the field). Two
    // listeners, not one: visibilitychange covers tab-switch/minimize/most
    // navigation reliably across browsers; pagehide is the backstop for the
    // cases (notably iOS Safari) where visibilitychange doesn't fire on an
    // actual unload.
    document.addEventListener('visibilitychange', () => this.flushIfHidden());
    window.addEventListener('pagehide', () => this.flushPendingAutosave());
  }

  flushIfHidden() {
    if (document.visibilityState === 'hidden') this.flushPendingAutosave();
  }

  flushPendingAutosave() {
    if (!this.saveTimer) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    this.autosave();
  }

  handleFieldInput(e) {
    const el = e.target.closest('[data-field-key]');
    if (!el || !this.form.contains(el)) return;
    this.state = setField(this.state, el.dataset.fieldKey, el.value);
    this.markEngaged();
    this.renderProgress();
    this.scheduleAutosave();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
    this.dock?.show();
  }

  renderProgress() {
    this.els.progressText.textContent = `${countAnswered(this.state)} of ${TOTAL_FIELDS} answered`;
    const has = hasAnyContent(this.state);
    this.els.copyBtn.disabled = !has;
    this.dock?.setReading(`${countAnswered(this.state)} of ${TOTAL_FIELDS} answered`);
    this.dock?.setComplete(has);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.sessionStore.save(toSave);
  }

  handleClear() {
    this.sessionStore.clear();
    this.state = createInitialState();
    this.form.reset();
    this.renderProgress();
    this.showActionFeedback('Cleared.');
  }

  async handleCopy() {
    const text = buildReflectionText(this.state);
    if (!text) return;
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(text);

    this.showActionFeedback(copied ? 'Copied!' : 'Could not copy - try selecting the text manually.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  showActionFeedback(message) {
    this.els.actionFeedback.textContent = message;
    setTimeout(() => {
      if (this.els.actionFeedback.textContent === message) this.els.actionFeedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!byId('listening-form')) return;
  new ListeningSessionFacilitator();
});
